import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";

/**
 * Regular expression patterns for different validation types.
 */
const patterns = {
  required: /.+/,
  email: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,}$/,
  phone: /^\+?\d+$/,
  numeric: /^\d+$/,
  alphanumeric: /^[a-zA-Z0-9]*$/,
  minLength: /(?=^.{0,}$)^.{8,}$/,
  maxLength: /(?=^.{0,}$)^.{0,15}$/,
  multipleEmails:
    /^\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(\s*[;,]\s*[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})*\s*$/,
  ipAddress: /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/,
};

/**
 * Retrieves the error message for a specific field and validation type.
 * @param {HTMLElement} field - The field element.
 * @param {string} validationType - The validation type.
 * @returns {string} - The error message.
 */
const getErrorMessage = (field, validationType) => {
  const errorMessage = field.getAttribute(
    `data-error-message-${validationType}`
  );
  const message =
    errorMessage ||
    (() => {
      switch (validationType) {
        case "required":
          return t("required");
        case "match":
          return t("match");
        case "email":
          return t("emailValidation");
        case "phone":
          return t("phoneValidation");
        case "numeric":
          return t("numeric");
        case "alphanumeric":
          return t("alphanumeric");
        case "min-length":
          return t("min-length", {
            count: field.getAttribute("data-validate-minlength"),
          });
        case "max-length":
          return t("max-length", {
            count: field.getAttribute("data-validate-maxlength"),
          });
        case "multiple-emails":
          return t("multiple-emails");
        case "ipaddress":
          return t("ipaddress");
        case "checkbox-group":
          return t("checkbox-group");
        case "max-value":
          return `Value can't be greater than ${field.getAttribute(
            "data-max-value"
          )}`;
        case "min-value":
          return `Value can't be smaller than ${field.getAttribute(
            "data-min-value"
          )}`;
        case "password-policy":
          return "Password does not meet the configured password policy";
        default:
          return "Invalid input";
      }
    })();

  const highlightError = field.getAttribute("data-mark-error");
  if (highlightError === "true") {
    _$(field).addClass("is-invalid");
  }

  const inlineMsg = field.getAttribute("data-inline-error");
  if (inlineMsg === "true") {
    const errorMsg = utils.generateHTML([
      {
        type: "div.error.text-sm",
        text: message,
        attrs: {
          "data-i18n-key": validationType,
        },
      },
    ]);

    _$(_$(field).parent().el()).el().append(errorMsg);
  }
  return message;
};

/**
 * Validates the form fields within a parent element.
 * @param {HTMLElement} parentEl - The parent element containing the form fields.
 * @param {boolean} onlyVisible - Determines if only visible fields should be validated.
 * @returns {string[]} - An array of error messages.
 */
export const validate = (parentEl, onlyVisible) => {
  if (!parentEl || !parentEl.querySelectorAll) return [];
  const dataValidateFields = [
    ...parentEl.querySelectorAll(
      "input[data-validate], textarea[data-validate], select[data-validate], div[data-validate].checkbox-parent"
    ),
  ];

  const inputEls = onlyVisible
    ? utils.filterVisible(dataValidateFields)
    : dataValidateFields;
  const errors = [];

  for (const inputEl of inputEls) {
    const validationTypes = inputEl.getAttribute("data-validate").split(",");

    const errorMsgs = inputEl.parentElement.querySelectorAll(".error");
    errorMsgs.forEach((element) => {
      element?.parentElement?.remove();
    });

    inputEl.classList.remove("is-invalid");

    if (
      !inputEl.classList.contains("dont-validate") &&
      !inputEl.getAttribute("data-dont-validate")
    ) {
      for (const validationType of validationTypes) {
        const errorsLengthBefore = errors.length;

        switch (validationType.trim()) {
          case "required":
            if (!inputEl.value) {
              errors.push(getErrorMessage(inputEl, validationType));
            }
            break;
          case "match":
            const matchEl = parentEl.querySelector(
              `input[name="${inputEl.dataset.match}"]`
            );
            if (matchEl && inputEl.value !== matchEl.value) {
              errors.push(getErrorMessage(inputEl, validationType));
            }
            break;
          case "email":
            if (!patterns.email.test(inputEl.value)) {
              errors.push(getErrorMessage(inputEl, validationType));
            }
            break;
          case "phone":
            if (!patterns.phone.test(inputEl.value)) {
              errors.push(getErrorMessage(inputEl, validationType));
            }
            break;
          case "numeric":
            if (!patterns.numeric.test(inputEl.value)) {
              errors.push(getErrorMessage(inputEl, validationType));
            }
            break;
          case "alphanumeric":
            if (!patterns.alphanumeric.test(inputEl.value)) {
              errors.push(getErrorMessage(inputEl, validationType));
            }
            break;
          case "no-special-chars":
            if (!patterns.noSpecialChars.test(inputEl.value)) {
              errors.push(getErrorMessage(inputEl, validationType));
            }
            break;
          case "min-length":
            const minLength = inputEl.dataset.minLength;
            if (minLength && inputEl.value.length < minLength) {
              errors.push(getErrorMessage(inputEl, validationType));
            }
            break;
          case "max-length":
            const maxLength = inputEl.dataset.maxLength;
            if (maxLength && inputEl.value.length > maxLength) {
              errors.push(getErrorMessage(inputEl, validationType));
            }
            break;
          case "multiple-emails":
            const emailList = inputEl.value.split(/[,;]/);
            for (const email of emailList) {
              if (email && !patterns.email.test(email.trim())) {
                errors.push(getErrorMessage(inputEl, validationType));
                break;
              }
            }
            break;
          case "ip-address":
            if (!patterns.ipAddress.test(inputEl.value)) {
              errors.push(getErrorMessage(inputEl, validationType));
            }
            break;
          case "checkbox-group":
            const groupEls = inputEl.querySelectorAll(`input[type="checkbox"]`);
            let isChecked = false;
            for (const groupEl of groupEls) {
              if (groupEl.checked) {
                isChecked = true;
                break;
              }
            }
            if (!isChecked) {
              if (groupEls.length === 1) {
                errors.push(getErrorMessage(inputEl, "required"));
              } else {
                errors.push(getErrorMessage(inputEl, validationType));
              }
            }
            break;
          case "max-value":
            const { maxValue } = inputEl?.dataset;
            const parsedMaxValue = Number(maxValue);
            const parsedMaxValueInput = Number(inputEl.value);

            if (parsedMaxValueInput > parsedMaxValue) {
              errors.push(getErrorMessage(inputEl, "max-value"));
            }
            break;
          case "min-value":
            const { minValue } = inputEl?.dataset;
            const parsedMinValue = Number(minValue);
            const parsedMinValueInput = Number(inputEl.value);

            if (parsedMinValueInput < parsedMinValue) {
              errors.push(getErrorMessage(inputEl, "min-value"));
            }
            break;
          case "password-policy":
            const passwordValue = inputEl.value || "";
            const toNumber = (value) => {
              const parsedValue = parseInt(value);
              return Number.isFinite(parsedValue) ? parsedValue : 0;
            };

            const passwordRules = {
              minNumbers: toNumber(inputEl.dataset.minNumbers),
              minLowers: toNumber(inputEl.dataset.minLowers),
              minUppers: toNumber(inputEl.dataset.minUppers),
              minSpecials: toNumber(inputEl.dataset.minSpecials),
              unsafeChars: inputEl.dataset.unsafeChars || "",
            };

            const characterCounts = {
              numbers: (passwordValue.match(/[0-9]/g) || []).length,
              lowers: (passwordValue.match(/[a-z]/g) || []).length,
              uppers: (passwordValue.match(/[A-Z]/g) || []).length,
              specials: (passwordValue.match(/[^A-Za-z0-9]/g) || []).length,
            };

            const hasUnsafeChars =
              passwordRules.unsafeChars &&
              passwordValue
                .split("")
                .some((char) => passwordRules.unsafeChars.includes(char));

            if (
              (passwordRules.minNumbers &&
                characterCounts.numbers < passwordRules.minNumbers) ||
              (passwordRules.minLowers &&
                characterCounts.lowers < passwordRules.minLowers) ||
              (passwordRules.minUppers &&
                characterCounts.uppers < passwordRules.minUppers) ||
              (passwordRules.minSpecials &&
                characterCounts.specials < passwordRules.minSpecials) ||
              hasUnsafeChars
            ) {
              errors.push(getErrorMessage(inputEl, validationType));
            }
            break;
          default:
            break;
        }

        const errorsLengthAfter = errors.length;

        if (errorsLengthAfter > errorsLengthBefore) {
          if (errorsLengthBefore === 0) {
            if (
              inputEl?.tagName === "INPUT" ||
              inputEl?.tagName === "TEXTAREA"
            ) {
              inputEl?.focus();
            } else {
              _$(inputEl).find("input", true)?.focus();
            }
          }

          break;
        }
      }
    }
  }

  const validateDiv = parentEl.querySelector("#verify-email-container");
  if (validateDiv) {
    validateDiv.classList.add("is-invalid");
    const verifiedEmail = validateDiv.getAttribute("data-validate-verified");
    if (verifiedEmail === "false") {
      validateDiv
        .querySelector("#email-verified-error-message")
        .classList.remove("is-hidden");
      errors.push("Email must be verified");
    }
  }

  return errors;
};
